import axios from 'axios';

export const isValidPlaylistPosition = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const parsePlaylistPosition = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return isValidPlaylistPosition(parsed) ? parsed : null;
};

export const toSafeIsoString = (value: Date | string | null | undefined): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  if (value !== null && value !== undefined && value !== '') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  return new Date().toISOString();
};

export const PAST_START_TIME_MESSAGE = 'Start time cannot be in the past. Pick a current or future time.';

export type DateQuickSelectOffset = {
  label: string;
  unit: 'months';
  value: number;
};

/** Quick-select pills on add-to-playlist start/end date pickers. */
export const ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS: DateQuickSelectOffset[] = [
  { label: '+1 Month', unit: 'months', value: 1 },
  { label: '+2 Months', unit: 'months', value: 2 },
  { label: '+3 Months', unit: 'months', value: 3 },
];

/** True when a chosen start time is already behind the clock; empty values mean "now". */
export const isPastStartTime = (value: Date | string | { valueOf(): number } | null | undefined): boolean => {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  const time = new Date(value.valueOf()).getTime();
  return !Number.isNaN(time) && time < Date.now();
};

/** Start time for createrequest — null means immediate placement (use now on backend). */
export const resolveStartTimeForApi = (options: {
  isFuturePlaylistPage: boolean;
  release: string;
  scheduleDate?: Date | null;
  formStartTime?: string | null;
}): string | null => {
  const { isFuturePlaylistPage, release, scheduleDate, formStartTime } = options;
  const scheduled = isFuturePlaylistPage || release === 'Later';

  if (scheduled) {
    return toSafeIsoString(scheduleDate ?? formStartTime);
  }

  if (formStartTime && formStartTime !== 'null' && String(formStartTime).trim()) {
    return formStartTime;
  }

  return null;
};

/** ISO timestamp stored on placement metadata. */
export const resolvePlacementStartTime = (options: {
  isFuturePlaylistPage: boolean;
  release: string;
  scheduleDate?: Date | null;
  formStartTime?: string | null;
}): string => resolveStartTimeForApi(options) ?? toSafeIsoString(new Date());

export interface ValidateAddToPlaylistInput {
  playlistIds: string[];
  position?: number | string | null;
  arrayPositions?: Array<number | string>;
  usesNumberPositionInput?: boolean;
  searchedSongData?: string;
  isFromCampaign?: boolean;
  endTime?: string | null;
  requireSongUri?: boolean;
}

export type ValidateAddToPlaylistResult =
  | { ok: true; position: number; positions: number[] }
  | { ok: false; message: string };

export const validateAddToPlaylistForm = (
  input: ValidateAddToPlaylistInput
): ValidateAddToPlaylistResult => {
  const playlistIds = (input.playlistIds || []).filter(Boolean);
  if (!playlistIds.length) {
    return { ok: false, message: 'Please select a playlist' };
  }

  const requireSongUri = input.requireSongUri !== false;
  const songUri = String(input.searchedSongData || '').trim();
  if (requireSongUri && !songUri) {
    return { ok: false, message: 'Please enter a Spotify track URI or URL' };
  }

  if (requireSongUri && !songUri.includes('spotify:track:')) {
    return { ok: false, message: 'Song URI is not correct' };
  }

  if (input.isFromCampaign && !input.endTime) {
    return { ok: false, message: 'End date is required for campaigns' };
  }

  if (input.usesNumberPositionInput) {
    const position = parsePlaylistPosition(input.position);
    if (position === null) {
      return { ok: false, message: 'Position is required' };
    }
    return { ok: true, position, positions: [position] };
  }

  const rawPositions = input.arrayPositions || [];
  const positions = rawPositions
    .map(value => parsePlaylistPosition(value))
    .filter((value): value is number => value !== null);

  if (!positions.length) {
    return { ok: false, message: 'Position is required' };
  }

  return { ok: true, position: positions[0], positions };
};

export interface InstantShareDetail {
  song_uri: string;
  selected_playlist_id: string;
  id?: string;
  position: number | string;
  started_time: Date | string;
  endTime?: string | null;
}

export const buildInstantShareDetail = (options: {
  songUri: string;
  playlistId: string;
  position: number | string;
  startTime?: string | Date | null;
  scheduleDate?: Date | null;
  endTime?: string | null;
}): InstantShareDetail => {
  const { songUri, playlistId, position, startTime, scheduleDate, endTime } = options;
  const startedTime = startTime || scheduleDate || new Date();

  return {
    song_uri: songUri,
    selected_playlist_id: playlistId,
    id: songUri?.split('spotify:track:')[1],
    position,
    started_time: startedTime,
    endTime: endTime ?? undefined,
  };
};

export const persistInstantShareDetail = (detail: InstantShareDetail): void => {
  localStorage.setItem('instantShareDetail', JSON.stringify(detail));
};

export const formatPlaylistAddError = (error: unknown, fallback?: string): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const apiMessage =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      (Array.isArray(error.response?.data?.errors)
        ? error.response?.data?.errors?.[0]?.msg
        : undefined);

    if (apiMessage) {
      return String(apiMessage);
    }

    const requestUrl = String(error.config?.url || '');
    if (requestUrl.includes('api.spotify.com')) {
      if (status === 401) {
        return 'Spotify session expired. Reconnect Spotify and try again.';
      }
      if (status === 403) {
        return 'Spotify rejected this add. Check that the playlist belongs to the selected profile.';
      }
      if (status === 404) {
        return 'Playlist or track was not found on Spotify.';
      }
      return 'Spotify could not add this song to the playlist.';
    }

    if (status === 403) {
      return 'You do not have access to add songs for this profile.';
    }
    if (status === 404) {
      return 'Playlist or profile was not found.';
    }
    if (status === 400) {
      return 'Could not save this playlist request. Check your inputs and try again.';
    }
    if (!error.response) {
      return 'Could not reach the server. Check your connection and try again.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback || 'Failed to add song to playlist. Please try again.';
};
